import { Award, Calendar, Eye, Gift, Loader2, Trophy, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { useNotification } from '../contexts/NotificationContext'
import { formatHumanDate } from '../lib/dateUtils'
import { claimPrize, fetchMyPrizes } from '../services/prizeService'

export default function Prizes() {
  const navigate = useNavigate()
  const notify = useNotification()
  const [prizes, setPrizes] = useState([])
  const [loading, setLoading] = useState(true)
  const [revealingPrize, setRevealingPrize] = useState(null)

  useEffect(() => {
    loadPrizes()
  }, [])

  const loadPrizes = async () => {
    try {
      const data = await fetchMyPrizes()

      if (data.success) {
        setPrizes(data.prizes || [])
      }
    } catch (err) {
      console.error('Error loading prizes:', err)
      notify.error('Failed to load prizes')
    } finally {
      setLoading(false)
    }
  }

  const handleReveal = async (prizeId) => {
    setRevealingPrize(prizeId)
    try {
      const data = await claimPrize({
        p_prize_id: prizeId
      })

      if (!data.success) throw new Error(data.error)

      // Update the prize in state with the revealed info
      const updatedPrize = {
        ...prizes.find(p => p.id === prizeId),
        claimed_at: data.prize.claimed_at,
        promo_code: data.prize.promo_code,
        redeem_url: data.prize.redeem_url,
        combined_redeem_url: data.prize.combined_redeem_url,
      }

      setPrizes(prizes.map(p => p.id === prizeId ? updatedPrize : p))
      notify.success('Prize revealed!')
    } catch (err) {
      console.error('Error revealing prize:', err)
      notify.error(err.message || 'Failed to reveal prize')
    } finally {
      setRevealingPrize(null)
    }
  }

  const getPlaceLabel = (place) => {
    switch (place) {
      case 1: return '1st Place'
      case 2: return '2nd Place'
      case 3: return '3rd Place'
      default: return `${place}th Place`
    }
  }

  const getPlaceIcon = (place) => {
    switch (place) {
      case 1: return <Trophy className="h-6 w-6 text-[var(--color-crown)]" />
      case 2: return <Award className="h-6 w-6 text-muted-foreground" />
      case 3: return <Award className="h-6 w-6 text-[var(--lemon-8)]" />
      default: return <Gift className="h-6 w-6 text-primary" />
    }
  }

  const formatDate = (dateString) => {
    return formatHumanDate(dateString)
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading prizes...</p>
          </div>
        </div>
      </Layout>
    )
  }

  const unclaimedPrizes = prizes.filter(p => !p.claimed_at)
  const claimedPrizes = prizes.filter(p => p.claimed_at)

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">My Prizes</h1>
          <p className="text-muted-foreground">
            View and redeem prizes you've won
          </p>
        </div>

        {prizes.length === 0 ? (
          <Card>
            <CardContent className="py-16">
              <div className="text-center">
                <Gift className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">No prizes yet</h3>
                <p className="text-muted-foreground mb-4">
                  Win bracket competitions to earn prizes from your groups!
                </p>
                <Button variant="outline" onClick={() => navigate('/groups')}>
                  Browse Groups
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Unclaimed Prizes */}
            {unclaimedPrizes.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
                  </span>
                  New Prizes to Claim
                </h2>
                <div className="space-y-4">
                  {unclaimedPrizes.map((prize) => (
                    <Card key={prize.id} className="border-primary/50">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 flex-shrink-0">
                            {getPlaceIcon(prize.place)}
                          </div>
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="font-semibold text-lg">{prize.name}</h3>
                              <Badge variant="secondary">{getPlaceLabel(prize.place)}{prize.winner_count > 1 ? ` (tie)` : ''}</Badge>
                            </div>
                            {prize.description && (
                              <p className="text-muted-foreground mb-2">{prize.description}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
                              {prize.group?.name && (
                                <span className="flex items-center gap-1">
                                  <Users className="h-4 w-4" />
                                  {prize.group.name}
                                </span>
                              )}
                              {prize.bracket?.name && (
                                <span className="flex items-center gap-1">
                                  <Trophy className="h-4 w-4" />
                                  {prize.bracket.name}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                Won {formatDate(prize.awarded_at)}
                              </span>
                            </div>
                            <Button
                              variant="primaryNeon"
                              onClick={() => handleReveal(prize.id)}
                              disabled={revealingPrize === prize.id}
                            >
                              {revealingPrize === prize.id ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Revealing...
                                </>
                              ) : (
                                <>
                                  <Eye className="h-4 w-4 mr-2" />
                                  Reveal Code
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Claimed Prizes */}
            {claimedPrizes.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4">Claimed Prizes</h2>
                <div className="space-y-4">
                  {claimedPrizes.map((prize) => (
                    <Card key={prize.id}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-muted flex-shrink-0">
                            {getPlaceIcon(prize.place)}
                          </div>
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="font-semibold text-lg">{prize.name}</h3>
                              <Badge variant="outline">{getPlaceLabel(prize.place)}{prize.winner_count > 1 ? ` (tie)` : ''}</Badge>
                              <Badge variant="secondary">Claimed</Badge>
                            </div>
                            {prize.description && (
                              <p className="text-muted-foreground mb-2">{prize.description}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
                              {prize.group?.name && (
                                <span className="flex items-center gap-1">
                                  <Users className="h-4 w-4" />
                                  {prize.group.name}
                                </span>
                              )}
                              {prize.bracket?.name && (
                                <span className="flex items-center gap-1">
                                  <Trophy className="h-4 w-4" />
                                  {prize.bracket.name}
                                </span>
                              )}
                            </div>

                            {/* Redemption Info */}
                            <div className="rounded-lg bg-muted/50 p-4">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                                {prize.promo_code && (
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-muted-foreground block">
                                      Promo Code
                                    </label>
                                    <code className="text-lg font-mono font-bold bg-background px-3 py-1 rounded border inline-block">
                                      {prize.promo_code}
                                    </code>
                                  </div>
                                )}
                                
                                <div className="flex flex-wrap gap-2">
                                  {prize.combined_redeem_url ? (
                                    <Button variant="primaryNeon" asChild>
                                      <a 
                                        href={prize.combined_redeem_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                      >
                                        Redeem Now
                                      </a>
                                    </Button>
                                  ) : prize.redeem_url ? (
                                    <Button variant="primaryNeon" asChild>
                                      <a 
                                        href={prize.redeem_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                      >
                                        Go to Merch
                                      </a>
                                    </Button>
                                  ) : null}
                                </div>
                              </div>

                              <p className="text-xs text-muted-foreground mt-3">
                                Claimed on {formatDate(prize.claimed_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

    </Layout>
  )
}
